import { makeObservable, observable, action, runInAction, reaction, computed } from 'mobx';
import {
  searchPrebuiltPrompts,
  addPrebuiltPrompt,
  updatePrebuiltPrompt,
  removePrebuiltPrompt,
  getAgentLinkage,
  getAgentsForPrompt,
  pinPromptToAgent,
  unpinPromptFromAgent,
  type PromptAgentLinkSummary,
  type AgentForPrompt,
} from '@/api/prebuiltPrompts';
import type { PrebuiltPrompt, PrebuiltPromptType } from '@/api/types';
import type { RootStore } from './RootStore';

// Library of "starter prompts" — quick-action chips in chat + admin agent
// instructions. Merchant-scoped via the auth context.

export class PrebuiltPromptStore {
  @observable items: PrebuiltPrompt[] = [];
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable search = '';
  @observable typeFilter: PrebuiltPromptType | null = null;
  /** Selected agent id from the AgentPickerDrawer; null = "Any agent". */
  @observable agentFilterId: number | null = null;

  // Linkage map: promptId → { agentCount, agentNames }. Loaded once on
  // merchant switch; refreshed after any pin/unpin to keep counts honest.
  @observable linkage: Map<number, PromptAgentLinkSummary> = new Map();
  @observable agentsForPromptId: number | null = null;
  @observable agentsForPrompt: AgentForPrompt[] = [];
  @observable isLoadingAgentsForPrompt = false;

  constructor(private root: RootStore) {
    makeObservable(this);

    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetch();
          void this.fetchLinkage();
        } else {
          runInAction(() => {
            this.items = [];
            this.totalCount = 0;
            this.linkage.clear();
          });
        }
      },
      { fireImmediately: true }
    );
  }

  @computed get totalLinkedAgents(): number {
    let n = 0;
    for (const v of this.linkage.values()) n += v.agentCount;
    return n;
  }

  agentCountFor(promptId: number): number {
    return this.linkage.get(promptId)?.agentCount ?? 0;
  }

  agentNamesFor(promptId: number): string[] {
    return this.linkage.get(promptId)?.agentNames ?? [];
  }

  @action setSearch(v: string): void { this.search = v; }
  @action setTypeFilter(v: PrebuiltPromptType | null): void { this.typeFilter = v; }

  /**
   * "Pinned to agent": the SERVER filters by the agent's id (api/PrebuiltPrompt/Search?agentId, through the link
   * table). It used to be matched here by display name against the first 100 agents of the practice: an agent past
   * them was not found, and the filter silently showed every prompt.
   */
  @action setAgentFilterId(v: number | null): void {
    this.agentFilterId = v;
    void this.fetch();
  }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await searchPrebuiltPrompts({
        search: this.search,
        type: this.typeFilter ?? undefined,
        agentId: this.agentFilterId ?? undefined,
        pageSize: 200,
      });
      runInAction(() => {
        this.items = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.items.length;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load prompts';
      });
    }
  }

  @action
  async create(input: Partial<PrebuiltPrompt>): Promise<PrebuiltPrompt> {
    const created = await addPrebuiltPrompt(input);
    void this.fetch();
    return created;
  }

  @action
  async update(input: Partial<PrebuiltPrompt>): Promise<PrebuiltPrompt> {
    const updated = await updatePrebuiltPrompt(input);
    void this.fetch();
    return updated;
  }

  @action
  async remove(id: number): Promise<void> {
    await removePrebuiltPrompt(id);
    runInAction(() => {
      this.items = this.items.filter((p) => p.id !== id);
      this.totalCount = Math.max(0, this.totalCount - 1);
      this.linkage.delete(id);
    });
  }

  // ── Linkage to agents ─────────────────────────────────────────────────

  @action
  async fetchLinkage(): Promise<void> {
    try {
      const summary = await getAgentLinkage();
      runInAction(() => {
        this.linkage = new Map(summary.map((s) => [s.promptId, s]));
      });
    } catch (err) {
      // Linkage is enrichment, not load-blocking.
      runInAction(() => {
        this.linkage.clear();
      });
      console.warn('Failed to load prompt-agent linkage:', err);
    }
  }

  @action
  async loadAgentsForPrompt(promptId: number): Promise<void> {
    this.agentsForPromptId = promptId;
    this.isLoadingAgentsForPrompt = true;
    try {
      const agents = await getAgentsForPrompt(promptId);
      runInAction(() => {
        this.agentsForPrompt = agents;
        this.isLoadingAgentsForPrompt = false;
      });
    } catch (err) {
      runInAction(() => {
        this.agentsForPrompt = [];
        this.isLoadingAgentsForPrompt = false;
      });
      throw err;
    }
  }

  @action
  async pinToAgent(promptId: number, agentId: number): Promise<void> {
    await pinPromptToAgent({ assistantAIId: agentId, prebuiltPromptId: promptId });
    void this.fetchLinkage();
    if (this.agentsForPromptId === promptId) {
      void this.loadAgentsForPrompt(promptId);
    }
  }

  @action
  async unpinFromAgent(linkRowId: number, promptId: number): Promise<void> {
    await unpinPromptFromAgent(linkRowId);
    void this.fetchLinkage();
    if (this.agentsForPromptId === promptId) {
      void this.loadAgentsForPrompt(promptId);
    }
  }

  @action
  clearAgentsForPrompt(): void {
    this.agentsForPromptId = null;
    this.agentsForPrompt = [];
  }
}
